import { apiClient } from '@/lib/api'
import { API_CONFIG } from '@/config/api'
import { orderLineOptionsSummary } from '@/lib/orderLineOptions'
import type { ShopShippingSettings } from '@/lib/shippingSettings'
import type { StructuredShippingAddress } from '@/lib/shippingAddressUtils'
import type { Event } from '@/types'

export interface ShopWebsiteConfig {
  shop_id: number
  name: string
  description?: string
  short_code?: string
  slug: string
  logo_url?: string
  favicon_url?: string
  og_image_url?: string
  city?: string
  country?: string
  address?: string
  phone?: string
  email?: string
  theme_slug?: string
  primary_color?: string
  secondary_color?: string
  font_family?: string
  /** Optional P0 design tokens (heading/body fonts, surface, motion, scoped custom CSS). */
  design_tokens?: {
    heading_font?: string
    body_font?: string
    surface_base?: 'default' | 'warm' | 'cool' | 'ink'
    motion_default?: 'none' | 'fade-up'
    header_overlay?: 'dark'
    header_show_name?: boolean
    header_logo_size?: 'sm' | 'md' | 'lg'
    header_cta_text?: string
    header_cta_link?: string
    header_brand_html?: string
    header_hide_cart?: boolean
    custom_css?: string
  } | null
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  google_analytics_id?: string
  /** Server-provided Maps Embed API key (HTTP referrer–restricted). */
  google_maps_embed_api_key?: string
  cdn_base_url?: string
  domain?: string
  is_published?: boolean
  storefront_access?: 'public' | 'preview' | 'coming_soon'
  is_preview?: boolean
  facebook_url?: string
  whatsapp_url?: string
  footer_variant?: 'full' | 'minimal' | 'links'
  /** end: menu + cart on the right; center: menu centered, cart right */
  header_nav_align?: 'end' | 'center'
  /** When true, list out-of-stock products with label; when false, hide from catalog */
  show_out_of_stock_on_shop?: boolean
  /**
   * When true, list products that have no online photo.
   * When false (default), hide them from the public catalog.
   */
  show_products_without_photos_on_shop?: boolean
  /** When false, catalog is display-only / contact-to-buy (no online cart). Default true. */
  storefront_purchasing_enabled?: boolean
  /**
   * When true (default, platform admin), guests must sign in before checkout.
   * When false, guest checkout via shop guest customer is allowed.
   */
  require_login_for_checkout?: boolean
  /** When false, storefront support chat widget is hidden. Default true. */
  support_chat_enabled?: boolean
  /** Shop-configured locales for multilingual storefront content fields */
  supported_content_locales?: string[]
}

type ShopWebsiteRequestOpts = {
  locale?: string
  previewToken?: string | null
}

function shopWebsiteParams(opts?: ShopWebsiteRequestOpts) {
  const params: Record<string, string> = {}
  if (opts?.locale) params.locale = opts.locale
  if (opts?.previewToken) params.preview = opts.previewToken
  return Object.keys(params).length > 0 ? params : undefined
}

export interface ShopNavSubitem {
  label: string
  /** In-page section id: navigates to current menu URL + `#anchor`. */
  anchor?: string
  /** Path segment after /shop/:slug/ (e.g. another CMS page slug or `products`). */
  path?: string
}

export interface ShopPage {
  page_id: number
  page_type: string
  slug: string
  title: string
  /** Optional short label for header/footer nav; falls back to title when empty. */
  nav_label?: string | null
  /** Optional intro line under the page title on the storefront. */
  page_subtitle?: string | null
  meta_description?: string
  content?: Record<string, unknown>
  sort_order: number
  is_published: boolean
  show_in_nav?: boolean
  /** Menu dropdown: each row is either `path` (another storefront URL segment) or `anchor` (same-page #). */
  nav_subitems?: ShopNavSubitem[]
  translations?: ShopPageTranslation[]
}

export interface ShopPageTranslation {
  locale: string
  title?: string | null
  nav_label?: string | null
  page_subtitle?: string | null
  meta_description?: string | null
  nav_subitem_labels?: string[] | null
}

export interface ShopPageSection {
  section_id: number
  section_type: string
  config?: Record<string, unknown>
  sort_order: number
}

export interface ShopPageWithSections extends ShopPage {
  sections: ShopPageSection[]
}

export interface ShopTheme {
  theme_id: number
  name: string
  slug: string
  description?: string
  preview_image_url?: string
}

export type StorefrontPaymentMethod = {
  provider: 'kpay' | 'fps' | 'stripe' | 'demopay'
  display_name: string
  environment?: 'uat' | 'production'
  is_default?: boolean
}

const shopWebsiteService = {
  async getByHost(previewToken?: string | null): Promise<{
    success: boolean
    data: {
      shop_id: number
      slug: string
      storefront_access?: string
      is_preview?: boolean
      soft_redirect?: { base_url: string; entry_path: string }
    }
  }> {
    return apiClient.get('/shop-website/by-host', {
      params: shopWebsiteParams({ previewToken }),
    })
  },

  sitemapUrl(slug: string, previewToken?: string | null): string {
    const base = `${API_CONFIG.BASE_URL.replace(/\/$/, '')}/shop-website/${encodeURIComponent(slug)}/sitemap`
    if (!previewToken) return base
    return `${base}?preview=${encodeURIComponent(previewToken)}`
  },

  async getBySlug(
    slug: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data: ShopWebsiteConfig }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}`, {
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async getPages(
    slug: string,
    locale?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data: ShopPage[] }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/pages`, {
      params: shopWebsiteParams({ locale, previewToken }),
    })
  },

  async getPageBySlug(
    slug: string,
    pageSlug: string,
    locale?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data: ShopPageWithSections }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/pages/${encodeURIComponent(pageSlug)}`, {
      params: shopWebsiteParams({ locale, previewToken }),
    })
  },

  async getThemes(): Promise<{ success: boolean; data: ShopTheme[] }> {
    return apiClient.get('/shop-website/themes')
  },

  async getEvents(slug: string, previewToken?: string | null): Promise<{ success: boolean; data: Event[] }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/events`, {
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async getEventByPublicId(
    slug: string,
    publicId: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data: Event }> {
    return apiClient.get(
      `/shop-website/${encodeURIComponent(slug)}/events/${encodeURIComponent(publicId)}`,
      { params: shopWebsiteParams({ previewToken }) }
    )
  },

  async getEventTicketTypes(
    slug: string,
    publicId: string,
    previewToken?: string | null,
  ): Promise<{ success: boolean; data: ShopEventTicketType[] }> {
    return apiClient.get(
      `/shop-website/${encodeURIComponent(slug)}/events/${encodeURIComponent(publicId)}/ticket-types`,
      { params: shopWebsiteParams({ previewToken }) },
    )
  },

  async getEventSeats(
    slug: string,
    publicId: string,
    ticketTypeId?: number,
    previewToken?: string | null,
    options?: { forMap?: boolean },
  ): Promise<{
    success: boolean
    data: Array<{
      event_seat_id: number
      event_ticket_type_id: number
      label: string
      sort_order: number
      status?: string
      section_code?: string | null
      row_key?: string | null
      seat_number?: number | null
      is_accessible?: boolean | null
      map_x?: number | string | null
      map_y?: number | string | null
    }>
  }> {
    return apiClient.get(
      `/shop-website/${encodeURIComponent(slug)}/events/${encodeURIComponent(publicId)}/seats`,
      {
        params: {
          ...shopWebsiteParams({ previewToken }),
          ...(ticketTypeId != null ? { ticket_type_id: ticketTypeId } : {}),
          ...(options?.forMap ? { for_map: '1' } : {}),
        },
      },
    )
  },

  async checkoutEventTickets(
    slug: string,
    publicId: string,
    data: {
      items: Array<{ ticket_type_id: number; event_seat_id?: number | null; quantity?: number }>
      attendee_name?: string
      attendee_email?: string
      notes?: string
    },
    sessionId?: string,
    previewToken?: string | null,
  ): Promise<{
    success: boolean
    code?: string
    data?: {
      order_id: number
      order_code: string
      redirect_url?: string
      redirect_html?: string
    }
    message?: string
  }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.post(
      `/shop-website/${encodeURIComponent(slug)}/events/${encodeURIComponent(publicId)}/ticket-checkout`,
      data,
      { headers, params: shopWebsiteParams({ previewToken }) },
    )
  },

  async getCart(
    slug: string,
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{
    success: boolean
    data: {
      items: CartItem[]
      session_id?: string
      checkout?: { provider: string; display_name?: string; environment: 'uat' | 'production' }
      payment_methods?: StorefrontPaymentMethod[]
      shipping_settings?: ShopShippingSettings
      pickup_locations?: ShopPickupLocation[]
      companion_bundle_preview?: {
        total_discount: number
        applied: Array<{
          companion_bundle_rule_id: number
          rule_name: string
          discount_amount: number
          matched_product_ids: number[]
        }>
      }
      membership_discount_total?: number
      pricing_subtotal?: number
    }
  }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/cart`, {
      headers,
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async addToCart(
    slug: string,
    data: { product_id: number; quantity?: number; options?: { selections: Record<number, number> } },
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; message: string; session_id?: string }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.post(`/shop-website/${encodeURIComponent(slug)}/cart`, data, {
      headers,
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async updateCartItem(
    slug: string,
    cartItemId: number,
    quantity: number,
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; message: string }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.put(
      `/shop-website/${encodeURIComponent(slug)}/cart/${cartItemId}`,
      { quantity },
      { headers, params: shopWebsiteParams({ previewToken }) }
    )
  },

  async removeCartItem(
    slug: string,
    cartItemId: number,
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; message: string }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.delete(`/shop-website/${encodeURIComponent(slug)}/cart/${cartItemId}`, {
      headers,
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async mergeCart(
    slug: string,
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data?: { merged: number; added: number } }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.post(
      `/shop-website/${encodeURIComponent(slug)}/cart/merge`,
      {},
      { headers, params: shopWebsiteParams({ previewToken }) }
    )
  },

  async validateCartCoupon(
    slug: string,
    data: { coupon_code?: string; customer_coupon_id?: number },
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{
    success: boolean
    message?: string
    data?: { applicable_discount: number; applicable_subtotal: number; message: string }
  }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.post(`/shop-website/${encodeURIComponent(slug)}/cart/validate-coupon`, data, {
      headers,
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async checkout(
    slug: string,
    data?: {
      notes?: string
      shipping_address?: string
      shipping_address_fields?: StructuredShippingAddress
      delivery_method?: 'pickup' | 'shipment' | null
      pickup_location_id?: number | null
      shipping_remark?: string
      shipping_country_code?: string
      payment_method?: 'kpay' | 'fps' | 'stripe' | 'demopay'
      coupon_code?: string
      customer_coupon_id?: number
    },
    sessionId?: string,
    previewToken?: string | null
  ): Promise<{
    success: boolean
    code?: string
    data?: {
      order_id: number
      order_code: string
      payment_method?: 'kpay' | 'fps' | 'stripe' | 'demopay'
      confirmation_url?: string
      redirect_url?: string
      redirect_html?: string
    }
    message?: string
  }> {
    const headers = sessionId ? { 'X-Cart-Session': sessionId } : undefined
    return apiClient.post(`/shop-website/${encodeURIComponent(slug)}/checkout`, data || {}, {
      headers,
      params: shopWebsiteParams({ previewToken }),
    })
  },

  async getCheckoutReturn(
    slug: string,
    orderId: number,
    previewToken?: string | null,
    kpayReturnParams?: Record<string, string>,
  ) {
    return apiClient.get<{
      success: boolean
      data?: {
        order?: {
          order_code?: string
          payment_status?: string
          final_amount?: number
          currency_code?: string
        }
        payment_intent?: {
          status?: string
          status_detail?: string | null
        }
        kpay_return?: {
          transactionState?: number
          transactionStateDesc?: string
        } | null
        event_tickets?: Array<{
          ticket_code: string
          seat_label?: string | null
          event_name?: string | null
          ticket_type_name?: string | null
        }>
        order_lines?: Array<{
          order_line_id: number
          product_name: string
          quantity: number
          unit_price: number
          total_line_amount: number
          line_type?: string
          description?: string | null
        }>
      }
    }>(`/shop-website/${encodeURIComponent(slug)}/checkout/return`, {
      params: {
        order_id: orderId,
        ...(kpayReturnParams || {}),
        ...shopWebsiteParams({ previewToken }),
      },
      timeout: 20000,
    })
  },

  async getDemoPaySession(
    slug: string,
    session: string,
    orderId?: number,
    previewToken?: string | null,
  ) {
    return apiClient.get<{
      success: boolean
      message?: string
      data?: {
        order_id: number
        order_code: string
        amount: number
        currency: string
        payment_intent_id: number
        status: string
      }
    }>(`/shop-website/${encodeURIComponent(slug)}/demo-pay`, {
      params: {
        session,
        ...(orderId != null ? { order_id: orderId } : {}),
        ...shopWebsiteParams({ previewToken }),
      },
    })
  },

  async confirmDemoPay(
    slug: string,
    session: string,
    orderId?: number,
    previewToken?: string | null,
  ) {
    return apiClient.post<{
      success: boolean
      message?: string
      data?: { order_id: number; payment_id: number; return_url?: string }
    }>(
      `/shop-website/${encodeURIComponent(slug)}/demo-pay/confirm`,
      { session, ...(orderId != null ? { order_id: orderId } : {}) },
      { params: shopWebsiteParams({ previewToken }) },
    )
  },

  async cancelDemoPay(
    slug: string,
    session: string,
    orderId?: number,
    previewToken?: string | null,
  ) {
    return apiClient.post<{
      success: boolean
      message?: string
      data?: { order_id: number; return_url?: string }
    }>(
      `/shop-website/${encodeURIComponent(slug)}/demo-pay/cancel`,
      { session, ...(orderId != null ? { order_id: orderId } : {}) },
      { params: shopWebsiteParams({ previewToken }) },
    )
  },

  async getOrderFpsQr(
    slug: string,
    orderId: number,
    orderCode?: string | null,
    previewToken?: string | null
  ): Promise<{
    success: boolean
    data?: {
      emv_payload: string | null
      presentation?: 'qr' | 'manual'
      amount: number
      currency: string
      order_code: string
      receive?: {
        account_type: string
        bank_code?: string
        bank_name?: string
        account_number?: string
        identifier_display?: string
      }
    }
    message?: string
  }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/checkout/fps-qr`, {
      params: {
        order_id: orderId,
        ...(orderCode ? { order_code: orderCode } : {}),
        ...shopWebsiteParams({ previewToken }),
      },
    })
  },

  async getBlogPosts(
    slug: string,
    opts?: { limit?: number; category?: string; previewToken?: string | null }
  ): Promise<{ success: boolean; data: ShopBlogPost[] }> {
    return apiClient.get(`/shop-website/${encodeURIComponent(slug)}/blog`, {
      params: {
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
        ...(opts?.category ? { category: opts.category } : {}),
        ...shopWebsiteParams({ previewToken: opts?.previewToken }),
      },
    })
  },

  async getBlogPostBySlug(
    slug: string,
    postSlug: string,
    previewToken?: string | null
  ): Promise<{ success: boolean; data: ShopBlogPost }> {
    return apiClient.get(
      `/shop-website/${encodeURIComponent(slug)}/blog/${encodeURIComponent(postSlug)}`,
      { params: shopWebsiteParams({ previewToken }) }
    )
  },

  async getProductReviews(
    slug: string,
    productId: number,
    previewToken?: string | null
  ): Promise<{
    success: boolean
    data?: {
      reviews: ProductReview[]
      summary: ProductReviewSummary
    }
  }> {
    return apiClient.get(
      `/shop-website/${encodeURIComponent(slug)}/products/${productId}/reviews`,
      { params: shopWebsiteParams({ previewToken }) }
    )
  },

  async submitProductReview(
    slug: string,
    productId: number,
    data: { author_name: string; author_email?: string; rating: number; body?: string },
    previewToken?: string | null
  ): Promise<{ success: boolean; message?: string }> {
    return apiClient.post(
      `/shop-website/${encodeURIComponent(slug)}/products/${productId}/reviews`,
      data,
      { params: shopWebsiteParams({ previewToken }) }
    )
  },

  async recordAnalyticsEvent(
    slug: string,
    data: {
      event_type: string
      page_path?: string
      product_id?: number
      order_id?: number
      session_id?: string
    },
    previewToken?: string | null
  ): Promise<{ success: boolean }> {
    return apiClient.post(`/shop-website/${encodeURIComponent(slug)}/analytics/event`, data, {
      params: shopWebsiteParams({ previewToken }),
    })
  },
}

export interface ShopEventTicketType {
  event_ticket_type_id: number
  event_id: number
  name: string
  code?: string | null
  description?: string | null
  unit_price: number
  currency_code: string
  capacity?: number | null
  available_count: number
  requires_seat_selection: boolean
  has_seating: boolean
}

export interface CartItem {
  cart_item_id: number
  product_id: number
  name: string
  subtitle?: string
  sku: string
  product_type?: string
  delivery_options?: unknown
  quantity: number
  unit_price: number
  /** Standard list price from product (for strikethrough when promo) */
  catalog_unit_price?: number
  options?: CartProductOptions
}

export interface ShopPickupLocation {
  pickup_location_id: number
  location_name: string
  is_default?: boolean
}

export type CartProductOptions = import('@/services/productService').CartProductOptions

export interface ShopBlogPost {
  blog_post_id: number
  title: string
  slug: string
  excerpt?: string | null
  body_html?: string | null
  category: string
  cover_image_url?: string | null
  published_at?: string | null
  created_at?: string
}

export interface ProductReview {
  product_review_id: number
  author_name: string
  rating: number
  body: string | null
  created_at: string
}

export interface ProductReviewSummary {
  average_rating: number
  review_count: number
}

export function cartItemOptionsSummary(item: CartItem): string | undefined {
  return orderLineOptionsSummary(item.options)
}

export default shopWebsiteService
